import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy
} from 'firebase/firestore';
import { getDb } from '../../firebase/utils';
import { OvertimeRequest, OvertimeSummary } from '../types/overtime';
import { useCurrentUser } from './useCurrentUser';

/**
 * 연장근로 요청 목록 조회
 */
export function useOvertimeRequests(employeeId?: string) {
  const { user } = useCurrentUser();
  const [requests, setRequests] = useState<OvertimeRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const targetEmployeeId = employeeId || user?.profile?.id;

  useEffect(() => {
    if (!targetEmployeeId) {
      setIsLoading(false);
      return;
    }

    const fetchRequests = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const q = query(
          collection(getDb(), 'overtimeRequests'),
          where('employeeId', '==', targetEmployeeId),
          orderBy('date', 'desc')
        );

        const snapshot = await getDocs(q);
        const requestList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as OvertimeRequest[];

        setRequests(requestList);
      } catch (err) {
        console.error('연장근로 요청 조회 실패:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequests();
  }, [targetEmployeeId]);

  return { requests, isLoading, error };
}

/**
 * 월별 연장근로 집계 조회
 */
export function useOvertimeSummary(
  employeeId?: string,
  year?: number,
  month?: number
) {
  const { user } = useCurrentUser();
  const [summary, setSummary] = useState<OvertimeSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const targetEmployeeId = employeeId || user?.profile?.id;
  const currentYear = year || new Date().getFullYear();
  const currentMonth = month || new Date().getMonth() + 1;

  useEffect(() => {
    if (!targetEmployeeId) {
      setIsLoading(false);
      return;
    }

    const fetchSummary = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // 해당 월의 시작일과 종료일
        const startDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
        const lastDay = new Date(currentYear, currentMonth, 0).getDate();
        const endDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

        const q = query(
          collection(getDb(), 'overtimeRequests'),
          where('employeeId', '==', targetEmployeeId),
          where('status', '==', 'approved'),
          where('date', '>=', startDate),
          where('date', '<=', endDate)
        );

        const snapshot = await getDocs(q);
        const requests = snapshot.docs.map(doc => doc.data()) as OvertimeRequest[];

        // 집계
        let totalOvertimeMinutes = 0;
        let totalWeekendMinutes = 0;
        let totalHolidayMinutes = 0;

        requests.forEach(request => {
          const minutes = request.approvedMinutes || 0;
          if (request.type === 'overtime') {
            totalOvertimeMinutes += minutes;
          } else if (request.type === 'weekend') {
            totalWeekendMinutes += minutes;
          } else if (request.type === 'holiday') {
            totalHolidayMinutes += minutes;
          }
        });

        const summaryData: OvertimeSummary = {
          employeeId: targetEmployeeId,
          employeeName: user?.profile?.displayName || '',
          year: currentYear,
          month: currentMonth,
          totalOvertimeMinutes,
          totalWeekendMinutes,
          totalHolidayMinutes,
          totalMinutes: totalOvertimeMinutes + totalWeekendMinutes + totalHolidayMinutes,
        };

        setSummary(summaryData);
      } catch (err) {
        console.error('연장근로 집계 조회 실패:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSummary();
  }, [targetEmployeeId, currentYear, currentMonth]);

  return { summary, isLoading, error };
}
